import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, 
  FileText, 
  BarChart3, 
  Users, 
  Settings,
  Search,
  Bell,
  ChevronRight,
  X,
  Menu,
  LogOut,
  Building2,
  Beaker,
  Plus,
  Eye,
  Edit2,
  ClipboardList,
  KeyRound
} from 'lucide-react';
import { platformAPI, userAPI, teamAPI, accessAPI, dataMappingAPI, pollsAPI, licensesAPI, accessControlAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EditProfileModal from '../components/Profile/EditProfileModal';
import AddDataSourceProviderModal from '../components/DataMapping/AddDataSourceProviderModal';
import AddMedicationModal from '../components/DataMapping/AddMedicationModal';
import AddRegimenModal from '../components/DataMapping/AddRegimenModal';
import RegimenViewEditModal from '../components/DataMapping/RegimenViewEditModal';
import DataSourceProviderViewEditModal from '../components/DataMapping/DataSourceProviderViewEditModal';
import MedicationViewEditModal from '../components/DataMapping/MedicationViewEditModal';
import CreatePollModal from '../components/Polls/CreatePollModal';
import PollResponseForm from '../components/Polls/PollResponseForm';
import PollResultsView from '../components/Polls/PollResultsView';
import PollNotificationsDropdown from '../components/Polls/PollNotificationsDropdown';
import ManageAccessControlModal from '../components/Licenses/ManageAccessControlModal';
import CreateLicenseModal from '../components/Licenses/CreateLicenseModal';
import { User, Team } from '../types';
import logoImage from '../images/logo.svg';
import toast from 'react-hot-toast';

interface PlatformStats {
  dataFinder: {
    totalDatasets: number;
    dataTypes: number;
    lastUpdated: string;
    status: string;
  };
  publicationSystem: {
    totalPapers: number;
    inReview: number;
    published: number;
    lastUpdated: string;
    status: string;
  };
}

interface ThemeConfig {
  themeColor: string;
  menuTheme: 'colorScheme' | 'primaryColor' | 'transparent';
}

type ViewMode = 'platforms' | 'users' | 'teams' | 'polls' | 'licenses' | 'dataSources' | 'medications' | 'regimens';

const DataEngineDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [activePlatform, setActivePlatform] = useState<string>('dataFinder');
  const [viewMode, setViewMode] = useState<ViewMode>('platforms');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [configSidebarVisible, setConfigSidebarVisible] = useState<boolean>(false);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [profileModalOpen, setProfileModalOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState<boolean>(false);
  const [canAccessLicenses, setCanAccessLicenses] = useState<boolean>(false);
  const [isCheckingLicenseAccess, setIsCheckingLicenseAccess] = useState<boolean>(false);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState<boolean>(false);
  const [userAccessControl, setUserAccessControl] = useState<{ 
    data_discovery_access?: 'Y' | 'N' | 'C' | null; 
    license_access?: 'Y' | 'N' | 'C' | null; 
  } | null>(null);
  const [showManageAccessModal, setShowManageAccessModal] = useState<boolean>(false);
  const [showCreateLicenseModal, setShowCreateLicenseModal] = useState<boolean>(false);
  const [usersPage, setUsersPage] = useState<number>(1);
  const [teamsPage, setTeamsPage] = useState<number>(1);
  const [dataSourcesPage, setDataSourcesPage] = useState<number>(1);
  const [medicationsPage, setMedicationsPage] = useState<number>(1);
  const [regimensPage, setRegimensPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [medications, setMedications] = useState<any[]>([]);
  const [regimens, setRegimens] = useState<any[]>([]);
  const [isLoadingDataSources, setIsLoadingDataSources] = useState<boolean>(false);
  const [isLoadingMedications, setIsLoadingMedications] = useState<boolean>(false);
  const [isLoadingRegimens, setIsLoadingRegimens] = useState<boolean>(false);
  const [showDataSourceForm, setShowDataSourceForm] = useState<boolean>(false);
  const [showMedicationForm, setShowMedicationForm] = useState<boolean>(false);
  const [showRegimenForm, setShowRegimenForm] = useState<boolean>(false);
  const [selectedRegimen, setSelectedRegimen] = useState<any | null>(null);
  const [regimenModalMode, setRegimenModalMode] = useState<'view' | 'edit'>('view');
  const [canAccessRegimens, setCanAccessRegimens] = useState<boolean>(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState<boolean>(false);
  const [selectedDataSource, setSelectedDataSource] = useState<any | null>(null);
  const [dataSourceModalMode, setDataSourceModalMode] = useState<'view' | 'edit'>('view');
  const [selectedMedication, setSelectedMedication] = useState<any | null>(null);
  const [medicationModalMode, setMedicationModalMode] = useState<'view' | 'edit'>('view');
  const [canAccessMedications, setCanAccessMedications] = useState<boolean>(false);
  const [isCheckingMedicationAccess, setIsCheckingMedicationAccess] = useState<boolean>(false);
  const [canAccessDataSources, setCanAccessDataSources] = useState<boolean>(false);
  const [isCheckingDataSourceAccess, setIsCheckingDataSourceAccess] = useState<boolean>(false);
  const [polls, setPolls] = useState<any[]>([]);
  const [isLoadingPolls, setIsLoadingPolls] = useState<boolean>(false);
  const [showCreatePollModal, setShowCreatePollModal] = useState<boolean>(false);
  const [selectedPoll, setSelectedPoll] = useState<any | null>(null);
  const [pollViewMode, setPollViewMode] = useState<'list' | 'respond' | 'results'>('list');
  const [pollNotificationCount, setPollNotificationCount] = useState<number>(0);
  const [userPolls, setUserPolls] = useState<any[]>([]);
  const [isLoadingUserPolls, setIsLoadingUserPolls] = useState<boolean>(false);
  const [showPollResponseModal, setShowPollResponseModal] = useState<boolean>(false);
  const [showPollResultsModal, setShowPollResultsModal] = useState<boolean>(false);
  const [showReminderSuccessModal, setShowReminderSuccessModal] = useState<boolean>(false);
  const [reminderSuccessMessage, setReminderSuccessMessage] = useState<string>('');
  const [userPollsPage, setUserPollsPage] = useState<number>(1);
  const [myPollsPage, setMyPollsPage] = useState<number>(1);
  const pollsPerPage = 3;
  const [dataFinderModal, setDataFinderModal] = useState<{
    open: boolean;
    allowed: boolean;
    message: string;
  }>({ open: false, allowed: false, message: '' });
  const [licenseModal, setLicenseModal] = useState<{
    open: boolean;
    license: any | null;
    allowed: boolean;
    message: string;
  }>({ open: false, license: null, allowed: false, message: '' });
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>({
    themeColor: 'purple',
    menuTheme: 'colorScheme'
  });

  const themeColors = [
    { name: 'indigo', color: '#6366F1' },
    { name: 'blue', color: '#3B82F6' },
    { name: 'purple', color: '#8B5CF6' },
    { name: 'teal', color: '#14B8A6' },
    { name: 'cyan', color: '#06b6d4' },
    { name: 'green', color: '#10b981' },
    { name: 'orange', color: '#f59e0b' },
    { name: 'pink', color: '#d946ef' },
  ];

  const activeTheme = themeColors.find(t => t.name === themeConfig.themeColor) || themeColors[2];

  // Set document title specifically for Data Engine Dashboard
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Data Intelligence Platform - research management platform: publications, source datasets discovery, and team collaboration';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Apply theme changes (CSS variables)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', activeTheme.color);
    root.style.setProperty('--accent-10', `${activeTheme.color}1A`);
    root.style.setProperty('--accent-15', `${activeTheme.color}26`);
  }, [activeTheme.color]);

  // Generate background gradient based on theme color
  const getPageBg = () => {
    const colorMap: Record<string, string> = {
      indigo: 'bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50',
      blue: 'bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50',
      purple: 'bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50',
      teal: 'bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50',
      cyan: 'bg-gradient-to-br from-cyan-50 via-blue-50 to-teal-50',
      green: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50',
      orange: 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50',
      pink: 'bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50',
    };
    return colorMap[themeConfig.themeColor] || 'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50';
  };
  const pageBg = getPageBg();
  const headerBg = 'bg-white border-gray-200';
  const headerText = 'text-gray-900';
  const mutedText = 'text-gray-600';
  const sidebarText = 'text-gray-700';
  const sidebarMuted = 'text-gray-500';
  const sidebarHover = 'hover:bg-gray-50';

  const menuBg =
    themeConfig.menuTheme === 'transparent'
      ? 'bg-transparent'
      : themeConfig.menuTheme === 'primaryColor'
        ? 'bg-[var(--accent-10)]'
        : 'bg-white';

  const sidebarBorder = 'border-gray-200';
  const iconColor = 'text-[var(--accent)]';
  const chipActive = 'bg-green-50 text-green-600';
  const chipError = 'bg-red-50 text-red-600';

  // Fetch platform stats
  useEffect(() => {
    if (viewMode !== 'platforms') return;
    
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await platformAPI.getPlatformStats();
        setStats(data);
      } catch (error: any) {
        console.error('Error fetching platform stats:', error);
        setError('Failed to load platform statistics. Please try again later.');
        setStats({
          dataFinder: {
            totalDatasets: 0,
            dataTypes: 0,
            lastUpdated: 'Unknown',
            status: 'error'
          },
          publicationSystem: {
            totalPapers: 0,
            inReview: 0,
            published: 0,
            lastUpdated: 'Unknown',
            status: 'error'
          }
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
    
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [viewMode]);

  // Fetch users
  useEffect(() => {
    if (viewMode !== 'users') return;
    
    const fetchUsers = async () => {
      try {
        setIsLoadingUsers(true);
        const usersData = await userAPI.getUsers();
        setUsers(usersData);
      } catch (error: any) {
        console.error('Error fetching users:', error);
        setError('Failed to load users. Please try again later.');
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [viewMode]);

  // Fetch teams
  useEffect(() => {
    if (viewMode !== 'teams') return;
    
    const fetchTeams = async () => {
      try {
        setIsLoadingTeams(true);
        const teamsData = await teamAPI.getTeams();
        setTeams(teamsData);
      } catch (error: any) {
        console.error('Error fetching teams:', error);
        setError('Failed to load teams. Please try again later.');
      } finally {
        setIsLoadingTeams(false);
      }
    };
    fetchTeams();
  }, [viewMode]);

  // Check access for licenses view (NONMEM)
  useEffect(() => {
    if (viewMode !== 'licenses') {
      setCanAccessLicenses(false);
      setIsCheckingLicenseAccess(false);
      return;
    }

    const checkAccess = async () => {
      try {
        setIsCheckingLicenseAccess(true);
        setError(null);
        const result = await accessAPI.canAccessNonmemLicense();
        setCanAccessLicenses(result.allowed);
        if (!result.allowed) {
          setError(result.reason || 'You are not currently authorized to view Licenses. Please contact an access controller (C) or an admin/superadmin.');
        }
      } catch (e) {
        console.error('Error checking license access:', e);
        setCanAccessLicenses(false);
        setError('Unable to verify access. Please try again later.');
      } finally {
        setIsCheckingLicenseAccess(false);
      }
    };

    checkAccess();
  }, [viewMode]);

  // Fetch user access control (to check if user has 'C' access)
  useEffect(() => {
    if (viewMode !== 'licenses' || !canAccessLicenses || !user?.id) return;
    const fetchUserAccessControl = async () => {
      try {
        const data = await accessControlAPI.getUserAccessControl(user.id);
        setUserAccessControl(data);
      } catch (e) {
        console.error('Error fetching user access control:', e);
        // Don't show error, just don't show admin buttons
      }
    };
    fetchUserAccessControl();
  }, [viewMode, canAccessLicenses, user?.id]);

  // Fetch licenses
  useEffect(() => {
    if (viewMode !== 'licenses' || !canAccessLicenses) return;
    const fetchLicenses = async () => {
      try {
        setIsLoadingLicenses(true);
        const data = await licensesAPI.listLicenses();
        setLicenses(data);
      } catch (e) {
        console.error('Error fetching licenses:', e);
        setError('Failed to load licenses. Please try again later.');
      } finally {
        setIsLoadingLicenses(false);
      }
    };
    fetchLicenses();
  }, [viewMode, canAccessLicenses]);

  // Fetch polls
  useEffect(() => {
    if (viewMode !== 'polls') return;
    
    const fetchPolls = async () => {
      try {
        setIsLoadingPolls(true);
        setIsLoadingUserPolls(true);
        const [createdPolls, userPollsData] = await Promise.all([
          pollsAPI.getPolls({ created_by_id: user?.id }),
          pollsAPI.getPollsForUser()
        ]);
        setPolls(createdPolls);
        setUserPolls(userPollsData);
      } catch (error: any) {
        console.error('Error fetching polls:', error);
        setError('Failed to load polls. Please try again later.');
      } finally {
        setIsLoadingPolls(false);
        setIsLoadingUserPolls(false);
      }
    };
    fetchPolls();
  }, [viewMode, user?.id]);

  // Fetch poll notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const result = await pollsAPI.getNotificationCount();
        setPollNotificationCount(result.count);
      } catch (error: any) {
        console.error('Error fetching poll notification count:', error);
      }
    };
    fetchNotificationCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check access for data sources view (UCSF Savic Lab only)
  useEffect(() => {
    if (viewMode !== 'dataSources') {
      // Reset access state when leaving this view
      setCanAccessDataSources(false);
      setIsCheckingDataSourceAccess(false);
      setError(null); // Clear any error messages
      return;
    }
    
    const checkAccess = async () => {
      try {
        setIsCheckingDataSourceAccess(true);
        setError(null); // Clear previous errors
        const result = await accessAPI.canAccessDataFinder();
        setCanAccessDataSources(result.allowed);
        if (!result.allowed) {
          setError(result.reason || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.');
        }
      } catch (error: any) {
        console.error('Error checking data sources access:', error);
        setCanAccessDataSources(false);
        setError('Unable to verify access. Please try again later.');
      } finally {
        setIsCheckingDataSourceAccess(false);
      }
    };

    checkAccess();
  }, [viewMode]);

  // Fetch data sources
  useEffect(() => {
    if (viewMode !== 'dataSources' || !canAccessDataSources) return;
    
    const fetchDataSources = async () => {
      try {
        setIsLoadingDataSources(true);
        const data = await dataMappingAPI.getDataSources();
        setDataSources(data);
      } catch (error: any) {
        console.error('Error fetching data sources:', error);
        setError('Failed to load data sources. Please try again later.');
      } finally {
        setIsLoadingDataSources(false);
      }
    };
    fetchDataSources();
  }, [viewMode, canAccessDataSources]);

  // Handle data source refresh after successful save
  const handleDataSourceSuccess = async () => {
    try {
      const data = await dataMappingAPI.getDataSources();
      setDataSources(data);
    } catch (error: any) {
      console.error('Error refreshing data sources:', error);
    }
  };

  // Handle medication refresh after successful save
  const handleMedicationSuccess = async () => {
    try {
      const data = await dataMappingAPI.getMedications();
      setMedications(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error refreshing medications:', error);
    }
  };

  const handleRegimenSuccess = async () => {
    try {
      const data = await dataMappingAPI.getRegimens();
      setRegimens(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error refreshing regimens:', error);
    }
  };

  // Check access for medications view (UCSF Savic Lab only)
  useEffect(() => {
    if (viewMode !== 'medications') {
      // Reset access state when leaving this view
      setCanAccessMedications(false);
      setIsCheckingMedicationAccess(false);
      setError(null); // Clear any error messages
      return;
    }
    
    const checkAccess = async () => {
      try {
        setIsCheckingMedicationAccess(true);
        setError(null); // Clear previous errors
        const result = await accessAPI.canAccessDataFinder();
        setCanAccessMedications(result.allowed);
        if (!result.allowed) {
          setError(result.reason || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.');
        }
      } catch (error: any) {
        console.error('Error checking medications access:', error);
        setCanAccessMedications(false);
        setError('Unable to verify access. Please try again later.');
      } finally {
        setIsCheckingMedicationAccess(false);
      }
    };

    checkAccess();
  }, [viewMode]);

  // Clear error when switching to views that don't require access checks
  useEffect(() => {
    if (viewMode !== 'dataSources' && viewMode !== 'medications' && viewMode !== 'regimens') {
      setError(null);
    }
  }, [viewMode]);

  // Fetch medications
  useEffect(() => {
    if (viewMode !== 'medications' || !canAccessMedications) return;
    
    const fetchMedications = async () => {
      try {
        setIsLoadingMedications(true);
        setError(null); // Clear previous errors
        const data = await dataMappingAPI.getMedications();
        setMedications(Array.isArray(data) ? data : []);
      } catch (error: any) {
        console.error('Error fetching medications:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Failed to load medications. Please try again later.';
        setError(errorMessage);
        setMedications([]); // Set empty array on error
      } finally {
        setIsLoadingMedications(false);
      }
    };
    fetchMedications();
  }, [viewMode, canAccessMedications]);

  // Check access for regimens view (UCSF Savic Lab only)
  useEffect(() => {
    if (viewMode !== 'regimens') {
      // Reset access state when leaving this view
      setCanAccessRegimens(false);
      setIsCheckingAccess(false);
      setError(null); // Clear any error messages
      return;
    }
    
    const checkAccess = async () => {
      try {
        setIsCheckingAccess(true);
        setError(null); // Clear previous errors
        const result = await accessAPI.canAccessDataFinder();
        setCanAccessRegimens(result.allowed);
        if (!result.allowed) {
          setError(result.reason || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.');
        }
      } catch (error: any) {
        console.error('Error checking regimens access:', error);
        setCanAccessRegimens(false);
        setError('Unable to verify access. Please try again later.');
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, [viewMode]);

  // Fetch regimens
  useEffect(() => {
    if (viewMode !== 'regimens' || !canAccessRegimens) return;
    
    const fetchRegimens = async () => {
      try {
        setIsLoadingRegimens(true);
        setError(null); // Clear previous errors
        console.log('[DataEngineDashboard] Fetching regimens...');
        const data = await dataMappingAPI.getRegimens();
        console.log('[DataEngineDashboard] Regimens API response:', data);
        console.log('[DataEngineDashboard] Number of regimens:', Array.isArray(data) ? data.length : 0);
        setRegimens(Array.isArray(data) ? data : []);
      } catch (error: any) {
        console.error('[DataEngineDashboard] Error fetching regimens:', error);
        console.error('[DataEngineDashboard] Error response:', error.response);
        const errorMessage = error.response?.data?.error || error.message || 'Failed to load regimens. Please try again later.';
        setError(errorMessage);
        setRegimens([]); // Set empty array on error
      } finally {
        setIsLoadingRegimens(false);
      }
    };

    fetchRegimens();
  }, [viewMode, canAccessRegimens]);

  const handlePlatformClick = async (platform: string) => {
    if (platform === 'dataFinder') {
      try {
        const result = await accessAPI.canAccessDataFinder();
        if (result.allowed) {
          // Open styled modal explaining the access scope before redirect
          setDataFinderModal({
            open: true,
            allowed: true,
            message:
              'Source Data Discovery is only open to UCSF Savic Lab members. ' +
              'You are recognized as a member of an UCSF Savic Lab team. ' +
              'Click \"Continue\" to open Source Data Discovery.'
          });
        } else {
          const message =
            result.reason ||
            'Source Data Discovery is only open for UCSF Savic Lab members. ' +
            'Please ask an admin or superadmin to add your team membership in the Publication System Admin Panel.';
          toast.error(message);
          setDataFinderModal({
            open: true,
            allowed: false,
            message:
              message +
              '\n\nThis system is restricted to UCSF Savic Lab members only.'
          });
        }
      } catch (error) {
        console.error('Error checking Data Finder access:', error);
        const fallbackMessage =
          'Unable to verify access to Source Data Discovery at this time. ' +
          'If you believe you should have access as a UCSF Savic Lab member, ' +
          'please contact an admin or superadmin.';
        toast.error('Unable to verify access to Source Data Discovery. Please try again later.');
        setDataFinderModal({
          open: true,
          allowed: false,
          message: fallbackMessage
        });
      }
    } else if (platform === 'publicationSystem') {
      // Navigate to publication dashboard using React Router
      navigate('/publications');
    }
  };

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    setProfileModalOpen(true);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Close user menu / config when clicking outside or pressing ESC
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        setConfigSidebarVisible(false);
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-user-menu]')) return;
      setUserMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'User';
  const userInitials = user 
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  // Pagination component (similar to AdminPanel)
  const PaginationComponent = ({ 
    currentPage, 
    totalPages, 
    totalItems, 
    onPageChange, 
    itemsPerPage 
  }: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    itemsPerPage: number;
  }) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {startItem} to {endItem} of {totalItems} items
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    currentPage === pageNum
                      ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Calculate paginated data for users
  const usersTotalPages = Math.ceil(users.length / itemsPerPage);
  const usersStartIndex = (usersPage - 1) * itemsPerPage;
  const usersEndIndex = usersStartIndex + itemsPerPage;
  const paginatedUsers = users.slice(usersStartIndex, usersEndIndex);

  // Calculate paginated data for teams
  const teamsTotalPages = Math.ceil(teams.length / itemsPerPage);
  const teamsStartIndex = (teamsPage - 1) * itemsPerPage;
  const teamsEndIndex = teamsStartIndex + itemsPerPage;
  const paginatedTeams = teams.slice(teamsStartIndex, teamsEndIndex);

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <div className="flex h-screen overflow-hidden">
        {/* Left Sidebar Navigation */}
        <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} ${menuBg} border-r ${sidebarBorder} flex flex-col sidebar-scroll overflow-y-auto transition-all duration-300`}>
          {/* Logo */}
          <div className={`p-4 border-b ${sidebarBorder} relative`}>
            {sidebarCollapsed ? (
              <div className="flex items-center justify-center">
                <img
                  src={logoImage}
                  alt="Data Engine Logo"
                  className="h-8 w-8 object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <img
                  src={logoImage}
                  alt="Data Engine Logo"
                  className="w-full h-10 object-contain"
                />
              </div>
            )}
          </div>
          {/* RESEARCH PLATFORMS Section */}
          <div className="p-4">
            {!sidebarCollapsed && (
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sidebarMuted}`}>
                RESEARCH PLATFORMS
              </div>
            )}
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setViewMode('platforms');
                  setActivePlatform('dataFinder');
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'platforms' && activePlatform === 'dataFinder'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Data Discovery' : ''}
              >
                <Database className="w-5 h-5 text-purple-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Data Discovery</span>}
              </button>
              <button
                onClick={() => {
                  setViewMode('platforms');
                  setActivePlatform('publicationSystem');
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'platforms' && activePlatform === 'publicationSystem'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Publication System' : ''}
              >
                <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Publication System</span>}
              </button>
            </nav>
          </div>

          {/* USER MANAGEMENT Section */}
          <div className={`p-4 border-t ${sidebarBorder}`}>
            {!sidebarCollapsed && (
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sidebarMuted}`}>
                USER MANAGEMENT
              </div>
            )}
            <nav className="space-y-1">
              <button 
                onClick={() => {
                  setViewMode('users');
                  setUsersPage(1); // Reset to first page when switching views
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'users'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Users' : ''}
              >
                <Users className="w-5 h-5 text-gray-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Users</span>}
              </button>
              <button 
                onClick={() => {
                  setViewMode('teams');
                  setTeamsPage(1); // Reset to first page when switching views
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'teams'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Teams' : ''}
              >
                <Building2 className="w-5 h-5 text-gray-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Teams</span>}
              </button>
            </nav>
          </div>

          {/* TOOLS Section */}
          <div className={`p-4 border-t ${sidebarBorder}`}>
            {!sidebarCollapsed && (
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sidebarMuted}`}>
                TOOLS
              </div>
            )}
            <nav className="space-y-1">
              <button 
                onClick={() => {
                  setViewMode('polls');
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'polls'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Polls' : ''}
              >
                <ClipboardList className="w-5 h-5 text-blue-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Poll</span>}
              </button>
              <button 
                onClick={() => {
                  setViewMode('licenses');
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'licenses'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'License' : ''}
              >
                <KeyRound className="w-5 h-5 text-amber-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">License</span>}
              </button>
            </nav>
          </div>

          {/* DATA MAPPING Section */}
          <div className={`p-4 border-t ${sidebarBorder}`}>
            {!sidebarCollapsed && (
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sidebarMuted}`}>
                DATA MAPPING
              </div>
            )}
            <nav className="space-y-1">
              <button 
                onClick={() => {
                  setViewMode('dataSources');
                  setDataSourcesPage(1);
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'dataSources'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Source Data Provider Mapping' : ''}
              >
                <Database className="w-5 h-5 text-purple-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Source Data Provider</span>}
              </button>
              <button 
                onClick={() => {
                  setViewMode('medications');
                  setMedicationsPage(1);
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'medications'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Medication Mapping' : ''}
              >
                <Beaker className="w-5 h-5 text-purple-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Medications</span>}
              </button>
              <button 
                onClick={() => {
                  setViewMode('regimens');
                  setRegimensPage(1);
                }}
                className={`sidebar-item w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg text-left transition-all ${
                  viewMode === 'regimens'
                    ? `active ${sidebarText}`
                    : `${sidebarText} ${sidebarHover}`
                }`}
                title={sidebarCollapsed ? 'Regimen Mapping' : ''}
              >
                <Beaker className="w-5 h-5 text-purple-600 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-medium">Regimens</span>}
              </button>
            </nav>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header Bar */}
          <header className={`${headerBg} border-b h-16 flex items-center justify-between px-6`}>
            <div className="flex items-center gap-4">
              {/* Menu Toggle Button (Hamburger) */}
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-semibold ${headerText}`}>Savic lab Data Engine</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="hidden md:flex items-center gap-2 rounded-lg px-4 py-2 w-64 bg-gray-100">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="bg-transparent border-none outline-none text-sm flex-1 text-gray-900 placeholder-gray-400"
                />
              </div>
              {/* Notifications */}
              <PollNotificationsDropdown
                count={pollNotificationCount}
                onNotificationClick={() => {
                  setViewMode('polls');
                }}
                onPollClick={async (pollId: number) => {
                  try {
                    // Navigate to polls view
                    setViewMode('polls');
                    // Fetch and open the poll response modal
                    const pollDetails = await pollsAPI.getPoll(pollId);
                    setSelectedPoll(pollDetails);
                    setShowPollResponseModal(true);
                  } catch (error) {
                    console.error('Error fetching poll:', error);
                    toast.error('Failed to load poll');
                    // Still navigate to polls view even if poll fetch fails
                    setViewMode('polls');
                  }
                }}
              />
              {/* Settings Button */}
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfigSidebarVisible(true);
                  setUserMenuOpen(false);
                }}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                title="Settings"
                type="button"
              >
                <Settings className="w-5 h-5 text-[var(--accent)]" />
              </button>
              {/* User Menu */}
              <div className="relative" data-user-menu>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserMenuOpen(v => !v);
                    setConfigSidebarVisible(false);
                  }}
                  className="flex items-center gap-2 p-2 rounded-lg transition-colors hover:bg-gray-100"
                  title="User menu"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                    {userInitials}
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 border rounded-lg shadow-lg z-50 overflow-hidden bg-white border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className={`text-sm font-semibold ${headerText}`}>{userName}</div>
                      <div className="text-xs text-gray-500">{user?.role || 'User'}</div>
                    </div>
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={handleProfileClick}
                    >
                      Profile
                    </button>
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-6">
            {/* Welcome Section */}
            <div className="mb-6">
              <h1 className={`text-3xl font-bold mb-2 ${headerText}`}>
                {viewMode === 'platforms' && 'Welcome Back, ' + userName}
                {viewMode === 'users' && 'User Management'}
                {viewMode === 'teams' && 'Research Teams'}
                {viewMode === 'dataSources' && 'Source Data Provider Mapping'}
                {viewMode === 'medications' && 'Medication Mapping'}
                {viewMode === 'regimens' && 'Regimen Mapping'}
              </h1>
              <p className={mutedText}>
                {viewMode === 'platforms' && 'Quick overview of your research platforms'}
                {viewMode === 'users' && 'View users'}
                {viewMode === 'teams' && 'View research teams. Team membership and configuration can be managed by admin and superadmin users in the Publication System Admin Panel.'}
                {viewMode === 'dataSources' && 'View and manage data source providers linked to the Publication System Data Sources.'}
                {viewMode === 'medications' && 'View and manage medication mappings linked to the Source Data Discovery Medication table.'}
                {viewMode === 'regimens' && 'View and manage regimen mappings linked to the Source Data Discovery Regimen table.'}
              </p>
              {error && (
              <div className="mt-2 p-3 rounded-lg text-sm bg-yellow-50 border border-yellow-200 text-yellow-800">
                  ⚠️ {error}
                </div>
              )}
            </div>

            {/* Conditional Content Based on View Mode */}
            {viewMode === 'platforms' && (
            <>
            {/* Platform Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Data Discovery Card */}
              <div
                onClick={() => handlePlatformClick('dataFinder')}
                className="platform-card rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-white border-t-4 border-purple-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center">
                      <Database className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Data Discovery</h3>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${stats?.dataFinder.status === 'error' ? chipError : chipActive}`}>
                        {stats?.dataFinder.status === 'error' ? 'Error' : 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
                {isLoading ? (
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Datasets</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {stats?.dataFinder.totalDatasets || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Data Types</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {stats?.dataFinder.dataTypes || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Last Updated</span>
                      <span className="text-sm font-medium text-gray-700">
                        {stats?.dataFinder.lastUpdated || 'Unknown'}
                      </span>
                    </div>
                  </div>
                )}
                <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                  <span>Source Data</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Publication System Card */}
              <div
                onClick={() => handlePlatformClick('publicationSystem')}
                className="platform-card rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 bg-white border-t-4 border-green-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Publication System</h3>
                    <span className={`text-xs font-medium px-2 py-1 rounded ${stats?.publicationSystem.status === 'error' ? chipError : chipActive}`}>
                      {stats?.publicationSystem.status === 'error' ? 'Error' : 'Active'}
                    </span>
                    </div>
                  </div>
                </div>
                {isLoading ? (
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Papers</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {stats?.publicationSystem.totalPapers || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">In Progress</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {stats?.publicationSystem.inReview || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Manuscript</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {stats?.publicationSystem.published || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Updated</span>
                    <span className="text-sm font-medium text-gray-700">
                      {stats?.publicationSystem.lastUpdated || 'Unknown'}
                    </span>
                  </div>
                </div>
                </>
                )}
                <button className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                  <span>View Publication</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            </>
            )}

            {/* Users List View */}
            {viewMode === 'users' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {isLoadingUsers ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading users...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                    <p className="text-gray-600">Users will appear here when they register.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Institution
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedUsers.map((userItem) => (
                          <tr key={userItem.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {userItem.firstName || userItem.first_name} {userItem.lastName || userItem.last_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{userItem.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                userItem.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                                userItem.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {userItem.role === 'superadmin' ? 'Super Admin' :
                                 userItem.role === 'admin' ? 'Admin' : 'User'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{userItem.institution || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const isActive: any = userItem.isActive !== undefined ? userItem.isActive : userItem.is_active;
                                const isUserActive = isActive === true || isActive === 1 || isActive === null || isActive === undefined;
                                
                                return (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isUserActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {isUserActive ? 'Active' : 'Inactive'}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <PaginationComponent
                      currentPage={usersPage}
                      totalPages={usersTotalPages}
                      totalItems={users.length}
                      onPageChange={setUsersPage}
                      itemsPerPage={itemsPerPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Teams List View */}
            {viewMode === 'teams' && (
              <div>
                {isLoadingTeams ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading teams...</p>
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                    <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Teams Found</h3>
                    <p className="text-gray-600">
                      Teams will appear here when they are created.
                    </p>
                  </div>
                ) : (
                  <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedTeams.map((team) => (
                      <div key={team.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900 text-sm">
                                {team.full_name || team.team_name || `Team ${team.id}`}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {(team.member_count || 0)} members
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {team.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {team.description}
                          </p>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          {team.created_by_name && `Created by ${team.created_by_name}`}
                        </div>
                      </div>
                    ))}
                  </div>
                  <PaginationComponent
                    currentPage={teamsPage}
                    totalPages={teamsTotalPages}
                    totalItems={teams.length}
                    onPageChange={setTeamsPage}
                    itemsPerPage={itemsPerPage}
                  />
                  </>
                )}
              </div>
            )}

            {/* Polls List View */}
            {viewMode === 'polls' && (
              <div className="space-y-6">
                {/* Polls to Respond To */}
                {userPolls.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900">Polls for You</h2>
                      <p className="text-sm text-gray-600 mt-1">Polls that require your response</p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {userPolls.slice((userPollsPage - 1) * pollsPerPage, userPollsPage * pollsPerPage).map((poll) => (
                          <div key={poll.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-medium text-gray-900">{poll.title}</h3>
                                {poll.description && (
                                  <p className="text-sm text-gray-600 mt-1">{poll.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span>Created by {poll.first_name} {poll.last_name}</span>
                                  {poll.deadline && (
                                    <span>Deadline: {(() => {
                                      // Parse deadline - handle both MySQL datetime and ISO string formats
                                      let dateStr = String(poll.deadline).trim();
                                      if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return '';
                                      
                                      // If it's an ISO string with 'T' or 'Z', extract the date/time parts
                                      // Format: "2026-01-27T17:00:00.000Z" or "2026-01-27T17:00:00"
                                      if (dateStr.includes('T')) {
                                        // Remove 'Z' if present and extract date/time
                                        dateStr = dateStr.replace('Z', '').replace(/\.\d+$/, ''); // Remove milliseconds and Z
                                        const [datePart, timePart] = dateStr.split('T');
                                        if (datePart && timePart) {
                                          const [year, month, day] = datePart.split('-').map(Number);
                                          const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
                                          const date = new Date(year, month - 1, day, hours, minutes, seconds);
                                          if (!isNaN(date.getTime())) {
                                            return date.toLocaleString('en-US', {
                                              year: 'numeric',
                                              month: 'numeric',
                                              day: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit',
                                              hour12: true
                                            });
                                          }
                                        }
                                      }
                                      
                                      // Check if it matches MySQL datetime format: "YYYY-MM-DD HH:MM:SS"
                                      const mysqlDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/;
                                      const match = dateStr.match(mysqlDateTimePattern);
                                      
                                      if (match) {
                                        try {
                                          // Parse manually to ensure local time (not UTC)
                                          const year = parseInt(match[1], 10);
                                          const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
                                          const day = parseInt(match[3], 10);
                                          const hours = parseInt(match[4], 10);
                                          const minutes = parseInt(match[5], 10);
                                          const seconds = parseInt(match[6], 10);
                                          
                                          // Create Date object using local timezone constructor
                                          const date = new Date(year, month, day, hours, minutes, seconds);
                                          
                                          // Verify the date is valid
                                          if (isNaN(date.getTime())) {
                                            return dateStr; // Fallback to original string
                                          }
                                          
                                          // Format as local time
                                          return date.toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'numeric',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          });
                                        } catch (e) {
                                          console.error('Error parsing deadline:', e, dateStr);
                                        }
                                      }
                                      
                                      // Last resort: try standard Date parsing
                                      try {
                                        const fallbackDate = new Date(dateStr);
                                        if (!isNaN(fallbackDate.getTime())) {
                                          return fallbackDate.toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'numeric',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          });
                                        }
                                      } catch (e) {
                                        // Ignore
                                      }
                                      
                                      return dateStr; // Last resort: return original string
                                    })()}</span>
                                  )}
                                  <span>{poll.response_count || 0} responses</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  // Parse MySQL datetime as local time for comparison
                                  let deadlineDate: Date | null = null;
                                  if (poll.deadline) {
                                    const dateStr = String(poll.deadline);
                                    if (dateStr.includes(' ') && !dateStr.includes('T') && !dateStr.includes('Z')) {
                                      try {
                                        // Parse manually to ensure local time (not UTC)
                                        const parts = dateStr.split(' ');
                                        if (parts.length >= 2) {
                                          const [datePart, timePart] = parts;
                                          const [year, month, day] = datePart.split('-').map(Number);
                                          const timeComponents = timePart.split(':');
                                          const [hours, minutes] = timeComponents.map(Number);
                                          const seconds = timeComponents[2] ? Number(timeComponents[2].split('.')[0]) : 0;
                                          deadlineDate = new Date(year, month - 1, day, hours, minutes, seconds);
                                        }
                                      } catch (e) {
                                        console.error('Error parsing deadline for comparison:', e, dateStr);
                                        deadlineDate = new Date(dateStr);
                                      }
                                    } else {
                                      deadlineDate = new Date(dateStr);
                                    }
                                  }
                                  const isPastDeadline = deadlineDate && deadlineDate < new Date();
                                  const allowAfterDeadline = poll.allow_responses_after_deadline === true || poll.allow_responses_after_deadline === 1;
                                  const canRespond = !isPastDeadline || allowAfterDeadline;
                                  
                                  if (poll.user_has_responded) {
                                    return (
                                      <>
                                        <span className="text-sm text-green-600 font-medium">✓ Responded</span>
                                        <button
                                          onClick={async () => {
                                            try {
                                              const pollDetails = await pollsAPI.getPoll(poll.id);
                                              setSelectedPoll(pollDetails);
                                              setShowPollResultsModal(true);
                                            } catch (error) {
                                              console.error('Error fetching poll:', error);
                                              toast.error('Failed to load poll results');
                                            }
                                          }}
                                          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:opacity-90"
                                        >
                                          View Results
                                        </button>
                                      </>
                                    );
                                  } else if (!canRespond) {
                                    return (
                                      <span className="text-sm text-red-600 font-medium">
                                        Deadline passed - No longer accepting responses
                                      </span>
                                    );
                                  } else {
                                    return (
                                      <button
                                        onClick={async () => {
                                          try {
                                            const pollDetails = await pollsAPI.getPoll(poll.id);
                                            setSelectedPoll(pollDetails);
                                            setShowPollResponseModal(true);
                                          } catch (error) {
                                            console.error('Error fetching poll:', error);
                                            toast.error('Failed to load poll');
                                          }
                                        }}
                                        className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90"
                                      >
                                        Respond
                                      </button>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {userPolls.length > pollsPerPage && (
                        <div className="mt-4">
                          <PaginationComponent
                            currentPage={userPollsPage}
                            totalPages={Math.ceil(userPolls.length / pollsPerPage)}
                            totalItems={userPolls.length}
                            onPageChange={setUserPollsPage}
                            itemsPerPage={pollsPerPage}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* My Created Polls */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">My Polls</h2>
                      <p className="text-sm text-gray-600 mt-1">Polls you created</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreatePollModal(true);
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create Poll</span>
                    </button>
                  </div>
                  {isLoadingPolls ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading polls...</p>
                    </div>
                  ) : polls.length === 0 ? (
                    <div className="text-center py-12">
                      <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No polls found</h3>
                      <p className="text-gray-600">Create your first poll to get started.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responses</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {polls.slice((myPollsPage - 1) * pollsPerPage, myPollsPage * pollsPerPage).map((poll) => (
                            <tr key={poll.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{poll.title}</div>
                                {poll.description && (
                                  <div className="text-sm text-gray-500 truncate max-w-md">{poll.description}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  poll.status === 'active' ? 'bg-green-100 text-green-800' :
                                  poll.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {poll.status === 'active' ? 'Active' : poll.status === 'closed' ? 'Closed' : 'Draft'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {poll.response_count || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {poll.deadline ? (() => {
                                  // Parse deadline - handle both MySQL datetime and ISO string formats
                                  let dateStr = String(poll.deadline).trim();
                                  if (!dateStr || dateStr === 'null' || dateStr === 'undefined') return '-';
                                  
                                  // If it's an ISO string with 'T' or 'Z', extract the date/time parts
                                  // Format: "2026-01-27T17:00:00.000Z" or "2026-01-27T17:00:00"
                                  if (dateStr.includes('T')) {
                                    // Remove 'Z' if present and extract date/time
                                    dateStr = dateStr.replace('Z', '').replace(/\.\d+$/, ''); // Remove milliseconds and Z
                                    const [datePart, timePart] = dateStr.split('T');
                                    if (datePart && timePart) {
                                      const [year, month, day] = datePart.split('-').map(Number);
                                      const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
                                      const date = new Date(year, month - 1, day, hours, minutes, seconds);
                                      if (!isNaN(date.getTime())) {
                                        return date.toLocaleString('en-US', {
                                          year: 'numeric',
                                          month: 'numeric',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          hour12: true
                                        });
                                      }
                                    }
                                  }
                                  
                                  // Check if it matches MySQL datetime format: "YYYY-MM-DD HH:MM:SS"
                                  const mysqlDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/;
                                  const match = dateStr.match(mysqlDateTimePattern);
                                  
                                  if (match) {
                                    try {
                                      // Parse manually to ensure local time (not UTC)
                                      const year = parseInt(match[1], 10);
                                      const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
                                      const day = parseInt(match[3], 10);
                                      const hours = parseInt(match[4], 10);
                                      const minutes = parseInt(match[5], 10);
                                      const seconds = parseInt(match[6], 10);
                                      
                                      // Create Date using local timezone constructor
                                      const date = new Date(year, month, day, hours, minutes, seconds);
                                      
                                      // Verify the date is valid
                                      if (isNaN(date.getTime())) {
                                        return dateStr; // Fallback to original string
                                      }
                                      
                                      return date.toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'numeric',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      });
                                    } catch (e) {
                                      console.error('Error parsing deadline:', e, dateStr);
                                    }
                                  }
                                  
                                  // Last resort: try standard Date parsing
                                  try {
                                    const fallbackDate = new Date(dateStr);
                                    if (!isNaN(fallbackDate.getTime())) {
                                      return fallbackDate.toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'numeric',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      });
                                    }
                                  } catch (e) {
                                    // Ignore
                                  }
                                  
                                  return dateStr; // Last resort: return original string
                                })() : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(poll.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {(() => {
                                  const now = new Date();
                                  // Parse MySQL datetime as local time
                                  let deadlineDate: Date | null = null;
                                  if (poll.deadline) {
                                    const dateStr = String(poll.deadline);
                                    if (dateStr.includes(' ') && !dateStr.includes('T') && !dateStr.includes('Z')) {
                                      try {
                                        // Parse manually to ensure local time (not UTC)
                                        const parts = dateStr.split(' ');
                                        if (parts.length >= 2) {
                                          const [datePart, timePart] = parts;
                                          const [year, month, day] = datePart.split('-').map(Number);
                                          const timeComponents = timePart.split(':');
                                          const [hours, minutes] = timeComponents.map(Number);
                                          const seconds = timeComponents[2] ? Number(timeComponents[2].split('.')[0]) : 0;
                                          deadlineDate = new Date(year, month - 1, day, hours, minutes, seconds);
                                        }
                                      } catch (e) {
                                        console.error('Error parsing deadline for notify check:', e, dateStr);
                                        deadlineDate = new Date(dateStr);
                                      }
                                    } else {
                                      deadlineDate = new Date(dateStr);
                                    }
                                  }
                                  const isDeadlinePassed = deadlineDate && deadlineDate < now;
                                  const allowResponsesAfterDeadline = poll.allow_responses_after_deadline === true || poll.allow_responses_after_deadline === 1;
                                  const canNotify = !isDeadlinePassed || allowResponsesAfterDeadline;
                                  // Ensure reminder_sent_count is a number
                                  const reminderCount = typeof poll.reminder_sent_count === 'number' 
                                    ? poll.reminder_sent_count 
                                    : parseInt(poll.reminder_sent_count as any, 10) || 0;
                                  const canSendReminder = canNotify && reminderCount < 2;
                                  const remainingReminders = Math.max(0, 2 - reminderCount);
                                  
                                  if (!canNotify) {
                                    return null;
                                  }
                                  
                                  if (reminderCount >= 2) {
                                    return (
                                      <span className="text-gray-400 text-sm mr-3" title="Reminder limit reached (2 times)">
                                        Notify (Limit Reached)
                                      </span>
                                    );
                                  }
                                  
                                  return (
                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        
                                        // Prevent multiple clicks
                                        if (!canSendReminder) {
                                          return;
                                        }
                                        
                                        try {
                                          const result: any = await pollsAPI.sendReminders(poll.id);
                                          
                                          // Check if request explicitly failed
                                          if (result && result.success === false) {
                                            toast.error(result.message || result.error || 'Failed to send reminder emails', { duration: 5000 });
                                            return;
                                          }
                                          
                                          // Success case - API call succeeded
                                          // Get the number of emails sent (default to 0 if not provided)
                                          const remindersSent = result?.remindersSent ?? 0;
                                          
                                          // Always show success message - format: "Reminder emails sent successfully! X email(s) sent"
                                          const successMessage = `Reminder emails sent successfully! ${remindersSent} email(s) sent`;
                                          
                                          // Show success modal with close button
                                          setReminderSuccessMessage(successMessage);
                                          setShowReminderSuccessModal(true);
                                          
                                          // Also try toast as backup
                                          toast.success(successMessage, { duration: 5000 });
                                          
                                          // Show warning separately if present (after success message)
                                          if (result?.warning) {
                                            setTimeout(() => {
                                              toast.error(result.warning, { duration: 5000 });
                                            }, 600);
                                          }
                                          
                                          // Immediately update the local state to reflect the new count
                                          // Only update if emails were actually sent
                                          if (remindersSent > 0 || result?.reminderCount !== undefined) {
                                            setPolls(prevPolls => {
                                              return prevPolls.map(p => {
                                                if (p.id === poll.id) {
                                                  const currentCount = typeof p.reminder_sent_count === 'number' 
                                                    ? p.reminder_sent_count 
                                                    : parseInt(String(p.reminder_sent_count || 0), 10);
                                                  const newCount = currentCount + 1;
                                                  return { ...p, reminder_sent_count: newCount };
                                                }
                                                return p;
                                              });
                                            });
                                          }
                                          
                                          // Then refresh from server to ensure consistency
                                          setTimeout(async () => {
                                            try {
                                              const updatedPolls = await pollsAPI.getPolls({ created_by_id: user?.id });
                                              setPolls(updatedPolls);
                                            } catch (refreshError) {
                                              console.error('Error refreshing polls:', refreshError);
                                            }
                                          }, 500);
                                        } catch (error: any) {
                                          const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Failed to send reminders';
                                          toast.error(errorMessage, { duration: 5000 });
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-800 mr-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-blue-600"
                                      title={`Send reminder emails to users who haven't responded (${remainingReminders} remaining)`}
                                      disabled={!canSendReminder}
                                    >
                                      Notify {remainingReminders > 0 && `(${remainingReminders} left)`}
                                    </button>
                                  );
                                })()}
                                <button
                                  onClick={async () => {
                                    try {
                                      const pollDetails = await pollsAPI.getPoll(poll.id);
                                      setSelectedPoll(pollDetails);
                                      setShowPollResultsModal(true);
                                    } catch (error) {
                                      console.error('Error fetching poll:', error);
                                      toast.error('Failed to load poll results');
                                    }
                                  }}
                                  className="text-[var(--accent)] hover:text-[var(--accent-dark)] mr-3"
                                >
                                  View Results
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {polls.length > pollsPerPage && (
                        <div className="px-6 py-4 border-t border-gray-200">
                          <PaginationComponent
                            currentPage={myPollsPage}
                            totalPages={Math.ceil(polls.length / pollsPerPage)}
                            totalItems={polls.length}
                            onPageChange={setMyPollsPage}
                            itemsPerPage={pollsPerPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Licenses View */}
            {viewMode === 'licenses' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Licenses</h2>
                    <p className="text-sm text-gray-600 mt-1">License tools available to you</p>
                  </div>
                  {userAccessControl?.license_access === 'C' && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowManageAccessModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                      >
                        <Users className="w-4 h-4" />
                        <span>Manage Access</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateLicenseModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Create License</span>
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {isCheckingLicenseAccess ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Checking access...</p>
                    </div>
                  ) : !canAccessLicenses ? (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                      <div className="text-sm font-medium text-yellow-800">Access restricted</div>
                      <div className="mt-1 text-sm text-yellow-800">
                        {error || 'You are not currently authorized to view Licenses.'}
                      </div>
                    </div>
                  ) : isLoadingLicenses ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading licenses...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {licenses.map((l) => {
                        // Format expiration date
                        const formatExpirationDate = (dateStr: string | null) => {
                          if (!dateStr) return 'No expiration';
                          try {
                            const date = new Date(dateStr);
                            const day = String(date.getDate()).padStart(2, '0');
                            const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                            const month = monthNames[date.getMonth()];
                            const year = date.getFullYear();
                            return `${day}-${month}-${year}`;
                          } catch {
                            return dateStr;
                          }
                        };

                        const expirationDisplay = formatExpirationDate(l.expires_on);
                        return (
                          <div key={l.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {l.display_name} {l.expires_on && `(expire on ${expirationDisplay})`}
                                </div>
                                {l.description && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    {l.description}
                                  </div>
                                )}
                                {!l.description && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    Available to authorized users only.
                                  </div>
                                )}
                              </div>
                              <button
                                className="bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                                onClick={async () => {
                                  // Check access before showing modal
                                  try {
                                    const result = await accessAPI.canAccessNonmemLicense();
                                    if (result.allowed) {
                                      setLicenseModal({
                                        open: true,
                                        license: l,
                                        allowed: true,
                                        message: `You are authorized to download ${l.display_name}. Click "Download" to proceed.`
                                      });
                                    } else {
                                      setLicenseModal({
                                        open: true,
                                        license: l,
                                        allowed: false,
                                        message: result.reason || 'You are not currently authorized to download this license.'
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Error checking license access:', error);
                                    toast.error('Unable to verify access. Please try again.');
                                  }
                                }}
                              >
                                {l.display_name} {l.expires_on && `(expire on ${expirationDisplay})`}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {licenses.length === 0 && (
                        <div className="text-sm text-gray-600">No licenses available.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Data Sources List View */}
            {viewMode === 'dataSources' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Data Sources</h2>
                  {canAccessDataSources && (
                    <button
                      type="button"
                      onClick={() => setShowDataSourceForm(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Provider</span>
                    </button>
                  )}
                </div>
                {canAccessDataSources && !isCheckingDataSourceAccess && (
                  <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Note:</span> Access to this page is restricted to UCSF Savic Lab team members.
                    </p>
                  </div>
                )}
                {isCheckingDataSourceAccess ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Checking access...</p>
                  </div>
                ) : !canAccessDataSources ? (
                  <div className="text-center py-12">
                    <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
                    <p className="text-gray-600">
                      {error || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.'}
                    </p>
                  </div>
                ) : isLoadingDataSources ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading data sources...</p>
                  </div>
                ) : dataSources.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No data sources found</h3>
                    <p className="text-gray-600">Data sources will appear here when they are added.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data_Sources_ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data_Sources_Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinical_or_Preclinical</th>
                          {canAccessDataSources && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dataSources.slice((dataSourcesPage - 1) * itemsPerPage, dataSourcesPage * itemsPerPage).map((item: any) => (
                          <tr 
                            key={item.id} 
                            className={canAccessDataSources ? "hover:bg-gray-50 cursor-pointer" : "hover:bg-gray-50"}
                            onClick={() => {
                              if (canAccessDataSources) {
                                setSelectedDataSource(item);
                                setDataSourceModalMode('view');
                              }
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800">{item.data_sources_id || `D${String(item.id).padStart(3, '0')}`}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{item.data_sources_description || item.description || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.contact_name || item.data_source_contact_name || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.clinical_or_preclinical || item.clinical_type || '-'}</td>
                            {canAccessDataSources && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setSelectedDataSource(item);
                                      setDataSourceModalMode('view');
                                    }}
                                    className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                    title="View"
                                  >
                                    <Eye className="h-4 w-4 text-gray-600" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedDataSource(item);
                                      setDataSourceModalMode('edit');
                                    }}
                                    className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 className="h-4 w-4 text-gray-600" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <PaginationComponent
                      currentPage={dataSourcesPage}
                      totalPages={Math.ceil(dataSources.length / itemsPerPage)}
                      totalItems={dataSources.length}
                      onPageChange={setDataSourcesPage}
                      itemsPerPage={itemsPerPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Medications List View */}
            {viewMode === 'medications' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Medications</h2>
                  {canAccessMedications && (
                    <button
                      type="button"
                      onClick={() => setShowMedicationForm(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Medication</span>
                    </button>
                  )}
                </div>
                {canAccessMedications && !isCheckingMedicationAccess && (
                  <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Note:</span> Access to this page is restricted to UCSF Savic Lab team members.
                    </p>
                  </div>
                )}
                {isCheckingMedicationAccess ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Checking access...</p>
                  </div>
                ) : !canAccessMedications ? (
                  <div className="text-center py-12">
                    <Beaker className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
                    <p className="text-gray-600">
                      {error || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.'}
                    </p>
                  </div>
                ) : isLoadingMedications ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading medications...</p>
                  </div>
                ) : medications.length === 0 ? (
                  <div className="text-center py-12">
                    <Beaker className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No medications found</h3>
                    <p className="text-gray-600">Medications will appear here when they are added.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Med_ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data_Sources_ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Generic Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">INN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abbrev1</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abbrev2</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {medications.slice((medicationsPage - 1) * itemsPerPage, medicationsPage * itemsPerPage).map((item: any) => (
                          <tr 
                            key={item.med_id || item.Med_ID || item.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedMedication(item);
                              setMedicationModalMode('view');
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800">
                              {item.med_id ||
                                item.Med_ID ||
                                (item.id ? `M${String(item.id).padStart(4, '0')}` : '-')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800">
                              {item.data_sources_id || item.Data_Sources_ID || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.generic_name || item.Generic_Name || item.name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.inn || item.International_Nonproprietary_Name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.abbreviation1 || item.Abbreviation1 || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.abbreviation2 || item.Abbreviation2 || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedMedication(item);
                                    setMedicationModalMode('view');
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedMedication(item);
                                    setMedicationModalMode('edit');
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <PaginationComponent
                      currentPage={medicationsPage}
                      totalPages={Math.ceil(medications.length / itemsPerPage)}
                      totalItems={medications.length}
                      onPageChange={setMedicationsPage}
                      itemsPerPage={itemsPerPage}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Regimens View */}
            {viewMode === 'regimens' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Regimens</h2>
                  {canAccessRegimens && (
                    <button
                      type="button"
                      onClick={() => setShowRegimenForm(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium shadow-sm hover:opacity-90 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Regimen</span>
                    </button>
                  )}
                </div>
                {canAccessRegimens && !isCheckingAccess && (
                  <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Note:</span> Access to this page is restricted to UCSF Savic Lab team members.
                    </p>
                  </div>
                )}
                {isCheckingAccess ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Checking access...</p>
                  </div>
                ) : !canAccessRegimens ? (
                  <div className="text-center py-12">
                    <Beaker className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
                    <p className="text-gray-600">
                      {error || 'Access to this page is limited to members of the UCSF Savic Lab teams. Please contact an admin or superadmin in the Publication System to be added to the appropriate team.'}
                    </p>
                  </div>
                ) : isLoadingRegimens ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading regimens...</p>
                  </div>
                ) : regimens.length === 0 ? (
                  <div className="text-center py-12">
                    <Beaker className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No regimens found</h3>
                    <p className="text-gray-600">Regimens will appear here when they are added.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regimen_ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Generic Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abbrev1</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abbrev2</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Med_IDs</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {regimens.slice((regimensPage - 1) * itemsPerPage, regimensPage * itemsPerPage).map((item: any) => (
                          <tr 
                            key={item.regimen_id || item.Regimen_ID || item.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedRegimen(item);
                              setRegimenModalMode('view');
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800">
                              {item.regimen_id || item.Regimen_ID || (item.id ? `R${String(item.id).padStart(4, '0')}` : '-')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.generic_name || item.Generic_Name || item.name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.abbreviation1 || item.Abbreviation1 || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.abbreviation2 || item.Abbreviation2 || '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {item.Med_IDs && Array.isArray(item.Med_IDs) && item.Med_IDs.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {item.Med_IDs.map((medId: string, idx: number) => (
                                    <span key={idx} className="inline-block px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-mono">
                                      {medId}
                                    </span>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedRegimen(item);
                                    setRegimenModalMode('view');
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  title="View"
                                >
                                  <Eye className="h-4 w-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRegimen(item);
                                    setRegimenModalMode('edit');
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <PaginationComponent
                      currentPage={regimensPage}
                      totalPages={Math.ceil(regimens.length / itemsPerPage)}
                      totalItems={regimens.length}
                      onPageChange={setRegimensPage}
                      itemsPerPage={itemsPerPage}
                    />
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Configuration Sidebar */}
      {configSidebarVisible && (
        <div className="fixed inset-0 z-[9999] pointer-events-none">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 pointer-events-auto"
            onClick={() => setConfigSidebarVisible(false)}
          ></div>
          
          {/* Sidebar */}
          <div className="fixed right-0 top-0 h-full w-80 shadow-2xl overflow-y-auto pointer-events-auto bg-white">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Configuration</h2>
                <button
                  onClick={() => setConfigSidebarVisible(false)}
                  className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Theme Colors */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold mb-3 text-gray-700">Theme Color</h3>
                <div className="grid grid-cols-4 gap-2">
                  {themeColors.map((theme) => (
                    <button
                      key={theme.name}
                      onClick={() => setThemeConfig({ ...themeConfig, themeColor: theme.name })}
                      className={`w-12 h-12 rounded-lg border-2 transition-all ${
                        themeConfig.themeColor === theme.name
                          ? 'border-gray-900 scale-110'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: theme.color }}
                      title={theme.name}
                    >
                      {themeConfig.themeColor === theme.name && (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Menu Theme */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold mb-3 text-gray-700">Menu Theme</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="menuTheme"
                      value="colorScheme"
                      checked={themeConfig.menuTheme === 'colorScheme'}
                      onChange={(e) => setThemeConfig({ ...themeConfig, menuTheme: e.target.value as any })}
                      className={`w-4 h-4 ${iconColor}`}
                    />
                    <span className="text-sm text-gray-700">Color Scheme</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="menuTheme"
                      value="primaryColor"
                      checked={themeConfig.menuTheme === 'primaryColor'}
                      onChange={(e) => setThemeConfig({ ...themeConfig, menuTheme: e.target.value as any })}
                      className={`w-4 h-4 ${iconColor}`}
                    />
                    <span className="text-sm text-gray-700">Primary Color</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="menuTheme"
                      value="transparent"
                      checked={themeConfig.menuTheme === 'transparent'}
                      onChange={(e) => setThemeConfig({ ...themeConfig, menuTheme: e.target.value as any })}
                      className={`w-4 h-4 ${iconColor}`}
                    />
                    <span className="text-sm text-gray-700">Transparent</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source Data Discovery Access Modal */}
      {dataFinderModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-[var(--accent)]" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Source Data Discovery Access
                </h2>
              </div>
              <button
                onClick={() => setDataFinderModal({ open: false, allowed: false, message: '' })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {dataFinderModal.message}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Note: Please contact an admin for access.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors"
                onClick={() => setDataFinderModal({ open: false, allowed: false, message: '' })}
              >
                Close
              </button>
              {dataFinderModal.allowed && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm rounded-lg bg-[var(--accent)] text-white hover:opacity-90 transition-colors"
                  onClick={() => {
                    setDataFinderModal({ open: false, allowed: false, message: '' });
                    // Pass user's name and email via URL parameters for data finder tracking
                    // Production: https://mechanisticminds.com/data-finder
                    // Local: http://localhost:3100
                    let baseUrl = process.env.REACT_APP_DATA_FINDER_SYS_URL;
                    if (!baseUrl) {
                      if (window.location.hostname === 'localhost') {
                        baseUrl = 'http://localhost:3100';
                      } else {
                        // Production: use /data-finder subdirectory
                        baseUrl = `${window.location.protocol}//${window.location.hostname}/data-finder`;
                      }
                    }
                    // Ensure baseUrl doesn't end with a slash
                    baseUrl = baseUrl.replace(/\/$/, '');
                    
                    if (user) {
                      const userName = `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email;
                      const userEmail = user.email || '';
                      const redirectUrl = `${baseUrl}?userName=${encodeURIComponent(userName)}&userEmail=${encodeURIComponent(userEmail)}`;
                      // Use window.location.href for full page navigation
                      window.location.href = redirectUrl;
                    } else {
                      window.location.href = baseUrl;
                    }
                  }}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* License Access Modal */}
      {licenseModal.open && licenseModal.license && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-amber-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  License Access
                </h2>
              </div>
              <button
                onClick={() => setLicenseModal({ open: false, license: null, allowed: false, message: '' })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm font-medium text-gray-900">
                {licenseModal.license.display_name}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {licenseModal.message}
              </p>
              {!licenseModal.allowed && (
                <p className="text-xs text-gray-500 mt-2">
                  Note: Please contact an admin for access.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors"
                onClick={() => setLicenseModal({ open: false, license: null, allowed: false, message: '' })}
              >
                Close
              </button>
              {licenseModal.allowed && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                  onClick={async () => {
                    try {
                      // Download license file using API with authentication
                      const blob = await licensesAPI.downloadLicense(licenseModal.license.id);
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      // Get filename from license code
                      link.download = `${licenseModal.license.code}-license.lic`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      setLicenseModal({ open: false, license: null, allowed: false, message: '' });
                      toast.success('License downloaded successfully');
                    } catch (error: any) {
                      console.error('Error downloading license:', error);
                      toast.error(error.response?.data?.error || 'Failed to download license');
                    }
                  }}
                >
                  Download
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <EditProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      {/* Add Data Source Provider Modal */}
      <AddDataSourceProviderModal
        isOpen={showDataSourceForm}
        onClose={() => setShowDataSourceForm(false)}
        onSuccess={handleDataSourceSuccess}
      />

      {/* Add Medication Modal */}
      <AddMedicationModal
        isOpen={showMedicationForm}
        onClose={() => setShowMedicationForm(false)}
        onSuccess={handleMedicationSuccess}
      />

      <AddRegimenModal
        isOpen={showRegimenForm}
        onClose={() => setShowRegimenForm(false)}
        onSuccess={handleRegimenSuccess}
      />

      <RegimenViewEditModal
        isOpen={selectedRegimen !== null}
        onClose={() => {
          setSelectedRegimen(null);
          setRegimenModalMode('view');
        }}
        onSuccess={() => {
          handleRegimenSuccess();
          setSelectedRegimen(null);
          setRegimenModalMode('view');
        }}
        regimen={selectedRegimen}
        mode={regimenModalMode}
      />

      <DataSourceProviderViewEditModal
        isOpen={selectedDataSource !== null}
        onClose={() => {
          setSelectedDataSource(null);
          setDataSourceModalMode('view');
        }}
        onSuccess={async () => {
          await handleDataSourceSuccess();
          setDataSourceModalMode('view');
        }}
        dataSource={selectedDataSource}
        mode={dataSourceModalMode}
      />

      <MedicationViewEditModal
        isOpen={selectedMedication !== null}
        onClose={() => {
          setSelectedMedication(null);
          setMedicationModalMode('view');
        }}
        onSuccess={() => {
          handleMedicationSuccess();
          setSelectedMedication(null);
          setMedicationModalMode('view');
        }}
        medication={selectedMedication}
        mode={medicationModalMode}
      />

      <EditProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      <CreatePollModal
        isOpen={showCreatePollModal}
        onClose={() => setShowCreatePollModal(false)}
        onSuccess={async () => {
          // Refresh polls list
          try {
            const [createdPolls, userPollsData] = await Promise.all([
              pollsAPI.getPolls({ created_by_id: user?.id }),
              pollsAPI.getPollsForUser()
            ]);
            setPolls(createdPolls);
            setUserPolls(userPollsData);
            // Refresh notification count
            const notifResult = await pollsAPI.getNotificationCount();
            setPollNotificationCount(notifResult.count);
          } catch (error) {
            console.error('Error refreshing polls:', error);
          }
        }}
      />

      <PollResponseForm
        isOpen={showPollResponseModal}
        onClose={() => {
          setShowPollResponseModal(false);
          setSelectedPoll(null);
        }}
        onSuccess={async () => {
          // Refresh polls list
          try {
            const [createdPolls, userPollsData] = await Promise.all([
              pollsAPI.getPolls({ created_by_id: user?.id }),
              pollsAPI.getPollsForUser()
            ]);
            setPolls(createdPolls);
            setUserPolls(userPollsData);
            // Refresh notification count
            const notifResult = await pollsAPI.getNotificationCount();
            setPollNotificationCount(notifResult.count);
          } catch (error) {
            console.error('Error refreshing polls:', error);
          }
        }}
        poll={selectedPoll}
      />

      <PollResultsView
        isOpen={showPollResultsModal}
        onClose={() => {
          setShowPollResultsModal(false);
          setSelectedPoll(null);
        }}
        poll={selectedPoll}
        currentUserId={user?.id || 0}
        onUpdate={async () => {
          // Refresh polls list
          try {
            const [createdPolls, userPollsData] = await Promise.all([
              pollsAPI.getPolls({ created_by_id: user?.id }),
              pollsAPI.getPollsForUser()
            ]);
            setPolls(createdPolls);
            setUserPolls(userPollsData);
          } catch (error) {
            console.error('Error refreshing polls:', error);
          }
        }}
      />

      {/* Reminder Success Modal */}
      {showReminderSuccessModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Success</h2>
              <button
                onClick={() => setShowReminderSuccessModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-900 text-lg">{reminderSuccessMessage}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowReminderSuccessModal(false)}
                className="px-4 py-2 bg-[var(--accent)] text-white rounded-lg hover:opacity-90 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ManageAccessControlModal
        isOpen={showManageAccessModal}
        onClose={() => setShowManageAccessModal(false)}
        onSuccess={async () => {
          // Refresh licenses and user access control
          try {
            if (viewMode === 'licenses' && canAccessLicenses) {
              const [licensesData, accessControlData] = await Promise.all([
                licensesAPI.listLicenses(),
                user?.id ? accessControlAPI.getUserAccessControl(user.id) : Promise.resolve(null)
              ]);
              setLicenses(licensesData);
              setUserAccessControl(accessControlData);
            }
          } catch (error) {
            console.error('Error refreshing after access update:', error);
          }
        }}
      />

      <CreateLicenseModal
        isOpen={showCreateLicenseModal}
        onClose={() => setShowCreateLicenseModal(false)}
        onSuccess={async () => {
          // Refresh licenses list
          try {
            if (viewMode === 'licenses' && canAccessLicenses) {
              const data = await licensesAPI.listLicenses();
              setLicenses(data);
            }
          } catch (error) {
            console.error('Error refreshing licenses:', error);
          }
        }}
      />
    </div>
  );
};

export default DataEngineDashboard;
